/**
 * Die Objektarten-Registry des Einrichten-Bereichs (Einrichten-Neubau P2).
 *
 * Kürzel (code) sind bewusst KEINE Formularfelder: die Server generieren sie
 * bei der Anlage selbst, gebraucht werden sie nur als Systemverdrahtung
 * (TWA-Ingest → Maschinen-Code, Schicht-Matcher, Snapshots). Das Objektblatt
 * zeigt sie nur in den zugeklappten „Technischen Angaben".
 *
 * Eine Beschreibung je Art treibt Liste und Objektblatt: welche Felder das
 * Formular hat, wohin geschrieben wird (die bewährten CRUD-Endpunkte, mit
 * ihren historischen Eigenheiten PATCH/PUT), und was der Zustandsschalter
 * bewirkt. Gelesen wird einheitlich über /api/einrichten/objekt/:art.
 *
 * Programmtypen haben bewusst keinen Eintrag — sie wirken nirgends
 * (EINRICHTEN-NEUBAU.md 5.2); das Feld erscheint nur bei der Programm-
 * Neuanlage, weil die Datenbank es noch verlangt.
 */

export interface FeldDef {
  key: string;
  label: string;
  typ: 'text' | 'zahl' | 'mehrzeilig' | 'auswahl' | 'farbe';
  pflicht?: boolean;
  /** Bei bestehenden Objekten unveränderlich (z. B. Kürzel) */
  festNachAnlage?: boolean;
  /** Nur im Anlege-Modus zeigen */
  nurBeiAnlage?: boolean;
  /** Options-Quelle für 'auswahl': eine andere Objektart */
  quelle?: string;
  hinweis?: string;
}

export interface ArtDef {
  einzahl: string;
  mehrzahl: string;
  /** CRUD-Basis (POST auf pfad, Update auf pfad/:id) */
  api: { pfad: string; updateMethode: 'PATCH' | 'PUT' };
  felder: FeldDef[];
  /** Wirkungs-Satz des Zustandsschalters */
  zustandsWirkung: string;
  /** Client-Plausibilität; Rückgabe = Fehlertext oder null */
  pruefe?: (form: Record<string, unknown>) => string | null;
  /** Feste System-Objekte — kein Anlegen-Knopf (Sammelposten) */
  ohneAnlegen?: boolean;
}

export const ARTEN: Record<string, ArtDef> = {
  maschinen: {
    einzahl: 'Maschine',
    mehrzahl: 'Maschinen',
    api: { pfad: '/api/admin/machines', updateMethode: 'PATCH' },
    felder: [
      { key: 'name', label: 'Name', typ: 'text', pflicht: true },
      {
        key: 'machineTypeId',
        label: 'Maschinentyp',
        typ: 'auswahl',
        quelle: 'maschinentypen',
        pflicht: true,
      },
      { key: 'capacityKg', label: 'Kapazität (kg)', typ: 'zahl', pflicht: true },
      { key: 'minLoadKg', label: 'Mindestbeladung (kg)', typ: 'zahl' },
      { key: 'maxLoadKg', label: 'Maximalbeladung (kg)', typ: 'zahl', pflicht: true },
      { key: 'overloadTolerance', label: 'Überlast-Toleranz (%)', typ: 'zahl' },
    ],
    zustandsWirkung:
      'Außer Betrieb nimmt die Maschine ab sofort aus der Auswahl an den Stationen. Erfasste Waschgänge bleiben unverändert und auswertbar.',
    pruefe: (f) => {
      const min = Number(f.minLoadKg ?? 0),
        kap = Number(f.capacityKg ?? 0),
        max = Number(f.maxLoadKg ?? 0);
      if (kap <= 0) return 'Die Kapazität muss größer als 0 sein.';
      if (min > kap) return 'Die Mindestbeladung liegt über der Kapazität.';
      if (max < kap) return 'Die Maximalbeladung liegt unter der Kapazität.';
      return null;
    },
  },
  maschinentypen: {
    einzahl: 'Maschinentyp',
    mehrzahl: 'Maschinentypen',
    api: { pfad: '/api/admin/machine-types', updateMethode: 'PATCH' },
    felder: [
      { key: 'name', label: 'Name', typ: 'text', pflicht: true },
      { key: 'sortOrder', label: 'Reihenfolge', typ: 'zahl' },
    ],
    zustandsWirkung:
      'Außer Betrieb nimmt den Typ aus der Auswahl bei neuen Maschinen. Bestehende Maschinen behalten ihn.',
  },
  programme: {
    einzahl: 'Programm',
    mehrzahl: 'Programme',
    api: { pfad: '/api/admin/programs', updateMethode: 'PATCH' },
    felder: [
      { key: 'number', label: 'Nummer', typ: 'text', pflicht: true },
      { key: 'name', label: 'Name', typ: 'text', pflicht: true },
      {
        key: 'programTypeId',
        label: 'Programmtyp',
        typ: 'auswahl',
        quelle: 'programmtypen',
        nurBeiAnlage: true,
        pflicht: true,
        hinweis: 'nur noch von der Datenbank verlangt — wird beim Cutover entfernt',
      },
      { key: 'dosNumber', label: 'Dosier-Nr.', typ: 'text' },
      { key: 'maxTemp', label: 'Max. Temperatur', typ: 'text' },
      { key: 'maxLoadPercent', label: 'Max. Beladung (%)', typ: 'zahl' },
      { key: 'remarks', label: 'Anmerkungen', typ: 'mehrzeilig' },
    ],
    zustandsWirkung:
      'Außer Betrieb nimmt das Programm aus der Auswahl an den WSA-Stationen. Erfasste Waschgänge bleiben unverändert.',
  },
  waescheklassen: {
    einzahl: 'Wäscheklasse',
    mehrzahl: 'Wäscheklassen',
    api: { pfad: '/api/admin/laundry-classes', updateMethode: 'PUT' },
    felder: [
      { key: 'name', label: 'Name', typ: 'text', pflicht: true },
      { key: 'description', label: 'Beschreibung', typ: 'mehrzeilig' },
      { key: 'sortOrder', label: 'Reihenfolge', typ: 'zahl' },
    ],
    zustandsWirkung:
      'Außer Betrieb nimmt die Wäscheklasse aus der Abfrage an den Stationen. Erfasste Einträge behalten sie.',
  },
  artikelgruppen: {
    einzahl: 'Artikelgruppe',
    mehrzahl: 'Artikelgruppen',
    api: { pfad: '/api/admin/article-groups', updateMethode: 'PUT' },
    felder: [
      { key: 'name', label: 'Name', typ: 'text', pflicht: true },
      { key: 'sortOrder', label: 'Reihenfolge', typ: 'zahl' },
    ],
    zustandsWirkung:
      'Außer Betrieb nimmt die Artikelgruppe aus der Abfrage an den Stationen. Erfasste Einträge behalten sie.',
  },
  sammelposten: {
    einzahl: 'Sammelposten',
    mehrzahl: 'Sammelposten',
    api: { pfad: '/api/admin/customer-groups', updateMethode: 'PATCH' },
    felder: [{ key: 'name', label: 'Name', typ: 'text', pflicht: true }],
    zustandsWirkung:
      'Außer Betrieb nimmt den Posten aus der WSA-Erfassung. Erfasste Waschgänge bleiben unverändert.',
    ohneAnlegen: true,
  },
  kundengruppen: {
    einzahl: 'Kundengruppe',
    mehrzahl: 'Kundengruppen',
    api: { pfad: '/api/admin/customer-groups', updateMethode: 'PATCH' },
    felder: [
      { key: 'name', label: 'Name', typ: 'text', pflicht: true },
      { key: 'sortOrder', label: 'Reihenfolge', typ: 'zahl' },
    ],
    zustandsWirkung:
      'Außer Betrieb nimmt die Gruppe aus der Auswahl an den Stationen. Kunden und erfasste Einträge bleiben unverändert.',
  },
  strecken: {
    einzahl: 'Strecke',
    mehrzahl: 'Strecken',
    api: { pfad: '/api/admin/lines', updateMethode: 'PATCH' },
    // Das Formular lebt im eigenen StreckeBlatt (Substrecken + Regel-Sätze);
    // dieser Eintrag versorgt nur die Liste und den Anlegen-Knopf.
    felder: [{ key: 'name', label: 'Name', typ: 'text', pflicht: true }],
    zustandsWirkung:
      'Außer Betrieb nimmt die Strecke samt Substrecken aus der Auswahl an den Stationen.',
  },
  stoerungskategorien: {
    einzahl: 'Störungskategorie',
    mehrzahl: 'Störungskategorien',
    api: { pfad: '/api/admin/disruption-categories', updateMethode: 'PATCH' },
    felder: [
      { key: 'name', label: 'Name', typ: 'text', pflicht: true },
      { key: 'sortOrder', label: 'Reihenfolge', typ: 'zahl' },
    ],
    zustandsWirkung:
      'Außer Betrieb nimmt die Kategorie aus der Auswahl beim Melden. Gemeldete Störungen behalten ihren Kategorie-Namen.',
  },
};

/** Options-Quellen für Auswahl-Felder, die keine eigene Kachel haben. */
export const AUSWAHL_QUELLEN: Record<string, string> = {
  maschinentypen: '/api/admin/machine-types',
  programmtypen: '/api/admin/program-types',
};
